import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { ColorOptions, TextStyleOptions } from '@df/ui/atomic/atomic-options';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';

let uid = 0;

export interface IPageComponentIndexedTextItem {
    title: string;
    content_title_fs: string;
    content_title_color?: string;
    content: string;
    content_color?: string;
}
export interface IPageComponentIndexedTextData extends NICmsContentComponent.IData {
    introduction?: string;
    items?: IPageComponentIndexedTextItem[];
    title_fs: string;
    intro_fs?: string;
    content_fs: string;
    show_bottom_numeration?: boolean;
}
@ICmsComponent<PageComponentIndexedTextComponent>({
    id: 'indexed-text',
    name: 'Indexed Text',
    tags: ['questions', 'points', 'list', 'text'],
    fields: [
        // general
        <NICmsContentField.IField>{
            key: 'introduction',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Introduction'
        },
        <NICmsContentField.IChoiceField>{
            key: 'intro_fs',
            type: NICmsContentField.EFieldType.choice,
            title: 'Introduction Font Size',
            options: TextStyleOptions,
            default: 'h5'
        },
        <NICmsContentField.IRepeaterField>{
            key: 'items',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Text sections',
            itemName: 'item',
            defaultSize: 2,
            fields: [
                <NICmsContentField.IField>{
                    key: 'title',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Title',
                    required: true
                },
                <NICmsContentField.IChoiceField>{
                    key: 'content_title_fs',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Content Title Font Style',
                    options: TextStyleOptions,
                    required: true,
                    default: 'h4'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'content_title_color',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Content Title Colour',
                    previewMode: 'color',
                    options: ColorOptions
                },
                {
                    key: 'content',
                    type: NICmsContentField.EFieldType.textarea,
                    title: 'Content',
                    required: true
                },
                <NICmsContentField.IChoiceField>{
                    key: 'content_color',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Content Colour',
                    previewMode: 'color',
                    options: ColorOptions
                }
            ],
            default: [
                {
                    content_title_fs: 'h4',
                    title: 'First title',
                    content: 'First content'
                },
                {
                    content_title_fs: 'h4',
                    title: 'Second title',
                    content: 'Second content'
                }
            ]
        },
        <NICmsContentField.IChoiceField>{
            key: 'title_fs',
            type: NICmsContentField.EFieldType.choice,
            title: 'Titles Font Size',
            options: TextStyleOptions,
            required: true,
            default: 'h2'
        },
        <NICmsContentField.IChoiceField>{
            key: 'content_fs',
            type: NICmsContentField.EFieldType.choice,
            title: 'Content Font Size',
            options: TextStyleOptions,
            required: true,
            default: 'q1'
        },
        {
            key: 'show_bottom_numeration',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Show number with titles in the content',
            default: true
        }
    ]
})
@Component({
    selector: 'page-component-indexed-text',
    templateUrl: './indexed-text.html',
    standalone: true,
    imports: [NgClass, ICmsClientModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageComponentIndexedTextComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: IPageComponentIndexedTextData;

    private scrollToService = inject(ScrollToService);

    private readonly _uid = `indexedTextComponent_${uid++}`;

    scrollToIndex(i: number) {
        this.scrollToService.scrollTo(`#${this.uid}item_${i}`, { offset: 120 });
    }

    get uid(): string {
        return this._uid;
    }
}
